import { jsx } from '@amazon/vinyl-tsx'
import { combineData, type ObservableValue } from '@amazon/vinyl-observable'
import { notSignedIn } from '@/auth/authRef'
import { getDeviceConfig, isMobile, isTv } from '@/device/deviceConfig'
import { phoneLayout$ } from '@/device/phoneLayout'
import { Icon } from '@/components/Icon'
import { buildGetAppDeeplink } from '@/header/getAppDeeplink'
import { openGetApp } from '@/header/openGetApp'
import { getString } from '@/header/i18n/bundle.generated'
import { reportShellClick, reportShellContentView } from '@/metrics/shellUiMetrics'

const REF_MARKER = 'dm_getapp_nav_unauth'
const CONTENT_NAME = 'getAppButtonUnauth'

/**
 * Whether the signed-out Get App button shows in the desktop header: never in the installed app or
 * on a TV, and on a computer's window only from the width the phone layout ends at.
 */
export function desktopGetAppVisible$(): ObservableValue<boolean> {
    if (isTv() || getDeviceConfig().installed) return notSignedIn().map(() => false)
    return combineData({ signedOut: notSignedIn(), phone: phoneLayout$() }).map(
        ({ signedOut, phone }) => signedOut && !phone
    )
}

/** Whether the phone top bar offers Get App: a mobile device at any width, signed out, never installed. */
export function mobileGetAppAvailable(): boolean {
    return isMobile() && !getDeviceConfig().installed
}

export interface GetAppButtonProps {
    readonly className: string
    readonly visible$: ObservableValue<boolean>
}

export function GetAppButton(props: GetAppButtonProps) {
    return (
        <button
            className={`${props.className} textButton`}
            visible={props.visible$}
            aria-label={getString('dmusic_get_app_button_text')}
            title={getString('dmusic_get_app_button_text')}
            onclick={() => {
                reportShellClick({ actionType: 'getApp', contentName: CONTENT_NAME })
                openGetApp(buildGetAppDeeplink(REF_MARKER, window.location.pathname))
            }}
            onConnect={() => {
                let reported = false
                return props.visible$.onData((shown) => {
                    if (!shown || reported) return
                    reported = true
                    reportShellContentView({ contentName: CONTENT_NAME })
                })
            }}
        >
            <Icon icon="phone" />
            <span className="label">{getString('dmusic_get_app_button_text')}</span>
        </button>
    )
}
